export type ImageFitRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type ViewportTransform = {
  scale: number;
  offsetX: number;
  offsetY: number;
};

export type ImageViewBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

function safePositive(value: number, fallback = 1) {
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

function roundPixel(value: number) {
  return Math.round(value * 1000) / 1000;
}

export function calculateContainImageRect(
  containerWidth: number,
  containerHeight: number,
  imageWidth: number,
  imageHeight: number,
): ImageFitRect {
  const safeContainerWidth = safePositive(containerWidth);
  const safeContainerHeight = safePositive(containerHeight);
  const safeImageWidth = safePositive(imageWidth);
  const safeImageHeight = safePositive(imageHeight);
  const scale = Math.min(
    safeContainerWidth / safeImageWidth,
    safeContainerHeight / safeImageHeight,
  );
  const width = safeImageWidth * scale;
  const height = safeImageHeight * scale;

  return {
    left: roundPixel((safeContainerWidth - width) / 2),
    top: roundPixel((safeContainerHeight - height) / 2),
    width: roundPixel(width),
    height: roundPixel(height),
  };
}

export function calculateCoverImageRect(
  containerWidth: number,
  containerHeight: number,
  imageWidth: number,
  imageHeight: number,
): ImageFitRect {
  const safeContainerWidth = safePositive(containerWidth);
  const safeContainerHeight = safePositive(containerHeight);
  const safeImageWidth = safePositive(imageWidth);
  const safeImageHeight = safePositive(imageHeight);
  const scale = Math.max(
    safeContainerWidth / safeImageWidth,
    safeContainerHeight / safeImageHeight,
  );
  const width = safeImageWidth * scale;
  const height = safeImageHeight * scale;

  return {
    left: roundPixel((safeContainerWidth - width) / 2),
    top: roundPixel((safeContainerHeight - height) / 2),
    width: roundPixel(width),
    height: roundPixel(height),
  };
}

function normalizeImageViewBox(
  viewBox: ImageViewBox | undefined,
  imageWidth: number,
  imageHeight: number,
): ImageViewBox {
  const safeImageWidth = safePositive(imageWidth);
  const safeImageHeight = safePositive(imageHeight);
  if (
    !viewBox ||
    !Number.isFinite(viewBox.x) ||
    !Number.isFinite(viewBox.y) ||
    !Number.isFinite(viewBox.width) ||
    !Number.isFinite(viewBox.height) ||
    viewBox.width <= 0 ||
    viewBox.height <= 0
  ) {
    return {
      x: 0,
      y: 0,
      width: safeImageWidth,
      height: safeImageHeight,
    };
  }

  const x = Math.max(0, Math.min(safeImageWidth, viewBox.x));
  const y = Math.max(0, Math.min(safeImageHeight, viewBox.y));
  return {
    x,
    y,
    width: Math.max(1, Math.min(safeImageWidth - x, viewBox.width)),
    height: Math.max(1, Math.min(safeImageHeight - y, viewBox.height)),
  };
}

export function calculateViewBoxContainImageRect(
  containerWidth: number,
  containerHeight: number,
  imageWidth: number,
  imageHeight: number,
  viewBox?: ImageViewBox,
): ImageFitRect {
  const safeContainerWidth = safePositive(containerWidth);
  const safeContainerHeight = safePositive(containerHeight);
  const safeImageWidth = safePositive(imageWidth);
  const safeImageHeight = safePositive(imageHeight);
  const safeViewBox = normalizeImageViewBox(
    viewBox,
    safeImageWidth,
    safeImageHeight,
  );
  const scale = Math.min(
    safeContainerWidth / safeViewBox.width,
    safeContainerHeight / safeViewBox.height,
  );

  return {
    left: roundPixel(
      (safeContainerWidth - safeViewBox.width * scale) / 2 -
        safeViewBox.x * scale,
    ),
    top: roundPixel(
      (safeContainerHeight - safeViewBox.height * scale) / 2 -
        safeViewBox.y * scale,
    ),
    width: roundPixel(safeImageWidth * scale),
    height: roundPixel(safeImageHeight * scale),
  };
}

export type ImageFitMode = 'contain' | 'fill' | 'cover' | 'viewBoxContain';

export function calculateImageFitRect(
  mode: ImageFitMode,
  containerWidth: number,
  containerHeight: number,
  imageWidth: number,
  imageHeight: number,
  viewBox?: ImageViewBox,
): ImageFitRect {
  if (mode === 'fill') {
    return {
      left: 0,
      top: 0,
      width: roundPixel(safePositive(containerWidth)),
      height: roundPixel(safePositive(containerHeight)),
    };
  }

  if (mode === 'cover') {
    return calculateCoverImageRect(
      containerWidth,
      containerHeight,
      imageWidth,
      imageHeight,
    );
  }

  if (mode === 'viewBoxContain') {
    return calculateViewBoxContainImageRect(
      containerWidth,
      containerHeight,
      imageWidth,
      imageHeight,
      viewBox,
    );
  }

  return calculateContainImageRect(
    containerWidth,
    containerHeight,
    imageWidth,
    imageHeight,
  );
}

export function calculateZoomedViewportTransform({
  previous,
  fitRect,
  localX,
  localY,
  scaleFactor,
  minScale = 0.25,
  maxScale = 8,
}: {
  previous: ViewportTransform;
  fitRect: ImageFitRect;
  localX: number;
  localY: number;
  scaleFactor: number;
  minScale?: number;
  maxScale?: number;
}): ViewportTransform {
  const nextScale = Math.max(
    minScale,
    Math.min(maxScale, previous.scale * scaleFactor),
  );
  const currentWidth = Math.max(0.0001, fitRect.width * previous.scale);
  const currentHeight = Math.max(0.0001, fitRect.height * previous.scale);
  const imageX = (localX - fitRect.left - previous.offsetX) / currentWidth;
  const imageY = (localY - fitRect.top - previous.offsetY) / currentHeight;

  return {
    scale: nextScale,
    offsetX: localX - fitRect.left - imageX * fitRect.width * nextScale,
    offsetY: localY - fitRect.top - imageY * fitRect.height * nextScale,
  };
}
